import React, { useEffect, useState } from 'react';
import { Avatar, Box, Divider, Grid, InputLabel, Stack, TextField, Typography, Tabs, Tab, Checkbox, Button } from '@mui/material';
import PageHeading from '../../Components/PageHeading';
import { AppColors } from '../../Utils/helper/AppColors';
import DragDropUpload from '../../Components/DragDropUpload';
// import { Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import CultService from '../../Services/CultService';
import toast from 'react-hot-toast';
import { HelperTexts } from '../../Utils/helper/HelperTexts';
import { DeleteIcon } from '../../Components/Icons/Icons';
import IconButton from '../../Components/IconButton';
import { mainOptions } from '../../Layout/mainOptions';
import { setCompany } from '../../store/reducers/appActions';

export default function Settings() {
	const company = useSelector((state) => state?.app?.company);
	const dispatch = useDispatch();
	const [details, setDetails] = useState({});
	const [loading, setLoading] = useState(false);
	const [tab, setTab] = useState(0);

	const [navOrder, setNavOrder] = useState([]);
	// const [draggingIdx, setDraggingIdx] = useState(null);
	// const [dragOverIdx, setDragOverIdx] = useState(null);
	// const [fontColor, setFontColor] = useState('#000');
	const [colorSettings, setColorSettings] = useState({
		fontColor: '#683FC7',
		buttonBackground: '#7F56D9',
		tertiary: '#BFA8F4',
		quaternary: '#D8CFF9',
		quinary: '#ECE7FC',
		// add more as needed
	});
	const [draggingLabel, setDraggingLabel] = useState(null);
	const [dragOverLabel, setDragOverLabel] = useState(null);

	// Load nav settings from localStorage on mount
	useEffect(() => {
		const navs = mainOptions('').map((item, idx) => ({
			label: item?.label,
			order: idx + 1,
			visible: true,
			...item,
		}));
		const saved = localStorage.getItem('navSettings');
		if (saved) {
			const { colorSettings: colorSettings, options } = JSON.parse(saved);
			setColorSettings(colorSettings || {});
			 // Build navOrder with both saved settings AND new items from mainOptions
        const navsOrdered = navs.map((nav) => {
            // Find if this nav item has saved settings
            const savedOption = options.find((opt) => opt?.label === nav?.label);
            return savedOption 
                ? { ...nav, visible: savedOption.visible } // Use saved visibility
                : { ...nav, visible: false }; // New items default to visible
        });
			setNavOrder(navsOrdered);
		} else {
			setNavOrder(navs);
		}
	}, [company?._id]);

	const handleDragEnd = (result) => {
		if (!result.destination) return;
		const items = Array.from(navOrder);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);
		const updated = items.map((item, idx) => ({ ...item, order: idx + 1 }));
		setNavOrder(updated);
	};

	const handleUpdate = async () => {
		setLoading(true);
		const payload = {
			name: details?.name,
			facebookLink: details?.facebookLink,
			instagramLink: details?.instagramLink,
			youtubeLink: details?.youtubeLink,
			xLink: details?.xLink,
			logo: details?.logo || company?.logo,
			linkedinLink: details?.linkedinLink,
		};
		try {
			let res = await CultService.updateCompany({ params: { id: company?._id }, data: payload });
			if (res.status == 200) {
				toast.success(HelperTexts.UPDATED_SUCCESSFULLY);
			}
		} catch (err) {
			toast.error(HelperTexts.SOMETHING_WENT_WRONG);
		} finally {
			setLoading(false);
		}
	};

	const uploadImage = async (e) => {
		const image = e.target.files[0];
		const data = new FormData();
		data.append('file', image);
		let res = await CultService.updateCompany({ params: { id: company?._id }, data });
		if (res.status == 200) {
			const updatedCompany = { ...company, logo: res.data?.data?.logo };
			dispatch(setCompany(updatedCompany));
			setDetails(updatedCompany);
			toast.success(HelperTexts.UPDATED_SUCCESSFULLY);
		}
	};

	const deleteImage = async () => {
    try {
        setLoading(true);
        const data = new FormData();
        data.append('file', null); // or you might need to append an empty file
        
        let res = await CultService.updateCompany({ 
            params: { id: company?._id }, 
            data: { logo: null } // or just send null/empty in the data
        });
        
        if (res.status == 200) {
            const updatedCompany = { ...company, logo: null };
            dispatch(setCompany(updatedCompany));
            setDetails(updatedCompany);
            toast.success('Logo deleted successfully');
        }
    } catch (err) {
        toast.error('Failed to delete logo');
    } finally {
        setLoading(false);
    }
};


	useEffect(() => {
		if (company) {
			setDetails({ ...company });
		}
	}, [company]);

	const handleChange = (e) => {
		setDetails({ ...details, [e.target.name]: e.target.value });
	};

	return (
		<Stack minHeight="100%" sx={{ '& .MuiDivider-root': { my: 2 }, padding: 2 }}>
			<PageHeading title="Settings" subTitle="Manage your company settings" />

			<Box sx={{ padding: 2, backgroundColor: AppColors.WHITE, borderRadius: '8px', mt: 2 }}>
				<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
					<Tab label="Company info" />
					<Tab label="Configuration" />
				</Tabs>

				{tab === 0 && (
					<>
						<Stack>
							<Typography variant="body1" fontSize={20}>
								Company info
							</Typography>
						</Stack>
						<Divider />
						<Grid container sx>
							<Grid item xs={4}>
								<InputLabel>Company Logo</InputLabel>
								<InputLabel sx={{ color: AppColors.TEXT_TERTIARY, fontWeight: 400 }}>
									This will be the logo of the company.
								</InputLabel>
							</Grid>
							<Grid item xs={6}>
								<Stack direction="row" alignItems="flex-start" gap={2}>
									<Stack position="relative">
										{details?.logo && (
											<IconButton
												onClick={deleteImage}
												rounded
												color={AppColors.TEXT_RED}
												size={22}
												icon={<DeleteIcon color={AppColors.WHITE} size={14} />}
												sx={{ position: 'absolute', right: 0, bottom: 0, zIndex: 1 }}
											/>
										)}
										<Avatar src={details?.logo} sx={{ height: 60, width: 60 }} />
									</Stack>
									<DragDropUpload
										onUpload={uploadImage}
										borderColor={colorSettings.buttonBackground}
										subText="SVG, PNG, JPG or GIF (max. 800x400px)"
									/>
								</Stack>
							</Grid>
						</Grid>
						<Divider />
						<Grid container>
							<Grid item xs={4}>
								<InputLabel>Facebook Link</InputLabel>
							</Grid>
							<Grid item xs={6}>
								<TextField name="facebookLink" onChange={handleChange} value={details?.facebookLink} fullWidth size="small" />
							</Grid>
						</Grid>
						<Divider />
						<Grid container>
							<Grid item xs={4}>
								<InputLabel>Instagram Link</InputLabel>
							</Grid>
							<Grid item xs={6}>
								<TextField name="instagramLink" onChange={handleChange} value={details?.instagramLink} fullWidth size="small" />
							</Grid>
						</Grid>
						<Divider />
						<Grid container>
							<Grid item xs={4}>
								<InputLabel>Youtube Link</InputLabel>
							</Grid>
							<Grid item xs={6}>
								<TextField name="youtubeLink" onChange={handleChange} value={details?.youtubeLink} fullWidth size="small" />
							</Grid>
						</Grid>
						<Divider />
						<Grid container>
							<Grid item xs={4}>
								<InputLabel>LinkedIn Link</InputLabel>
							</Grid>
							<Grid item xs={6}>
								<TextField name="linkedinLink" onChange={handleChange} value={details?.linkedinLink} fullWidth size="small" />
							</Grid>
						</Grid>
						<Divider />
						<Grid container>
							<Grid item xs={4}>
								<InputLabel>Twitter Link</InputLabel>
							</Grid>
							<Grid item xs={6}>
								<TextField name="xLink" onChange={handleChange} value={details?.xLink} fullWidth size="small" />
							</Grid>
						</Grid>
						<Divider />
						<Stack py={2} mt="auto" direction="row" gap={1} justifyContent="center" alignItems="center">
							<Button
								loading={loading}
								style={{ width: 100 }}
								onClick={handleUpdate}
								variant="contained"
								sx={{ backgroundColor: colorSettings.buttonBackground, color: AppColors.WHITE }}
							>
								Save
							</Button>
						</Stack>
					</>
				)}

				{tab === 1 && (
					<Box>
						<Typography fontWeight={600} fontSize={20} mb={2}>
							Navigation Order
						</Typography>
						<Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
							{navOrder
								.filter((item) => !!item.label)
								.map((item) => (
									<Box
										key={item.label}
										sx={{
											px: 2,
											py: 1,
											bgcolor: '#fff',
											border: '1px solid #ECE7FC',
											borderRadius: '4px',
											minWidth: 80,
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											textAlign: 'center',
											cursor: 'grab',
											boxShadow: draggingLabel === item?.label ? 2 : 0,
											opacity: draggingLabel === item?.label ? 0.5 : 1,
											position: 'relative',
										}}
										draggable
										onDragStart={() => setDraggingLabel(item.label)}
										onDragOver={(e) => {
											e.preventDefault();
											setDragOverLabel(item.label);
										}}
										onDrop={() => {
											if (!draggingLabel || draggingLabel === dragOverLabel) return;
											const items = [...navOrder];
											const fromIdx = items.findIndex((nav) => nav?.label === draggingLabel);
											const toIdx = items.findIndex((nav) => nav?.label === dragOverLabel);
											if (fromIdx === -1 || toIdx === -1) return;
											const [removed] = items.splice(fromIdx, 1);
											items.splice(toIdx, 0, removed);
											setNavOrder(items.map((item, i) => ({ ...item, order: i + 1 })));
											setDraggingLabel(null);
											setDragOverLabel(null);
										}}
										onDragEnd={() => {
											setDraggingLabel(null);
											setDragOverLabel(null);
										}}
									>
										<Checkbox
											checked={item.visible}
											onChange={() => {
												if (item.label === 'Settings') return toast.error('You cannot hide the Settings tab.');
												const updated = navOrder.map((nav) =>
													nav.label === item.label ? { ...nav, visible: !nav.visible } : nav,
												);
												setNavOrder(updated);
											}}
											sx={{ position: 'absolute', top: -2, right: -2 }}
										/>

										{item?.icon({
											size: 20,
											color: colorSettings.buttonBackground,
										})}
										<Typography fontFamily="poppins" fontWeight={400} fontSize={10} lineHeight="121%">
											{item?.label}
										</Typography>
									</Box>
								))}
						</Box>
						{/* Font Color Input */}
						<Box mt={3} mb={2}>
							<Typography fontWeight={500} fontSize={16} mb={1}>
								Color Settings :
							</Typography>
							<Stack direction="row" gap={2} alignItems="center">
								{[
									{ label: 'Button Color', key: 'buttonBackground' },
									{ label: 'Font Color', key: 'fontColor' },
									{ label: 'Tertiary Color', key: 'tertiary' },
									{ label: 'Quaternary Color', key: 'quaternary' },
									{ label: 'Quinary Color', key: 'quinary' },
								].map(({ label, key }) => (
									<Stack key={key} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
										<Box
											sx={{
												width: 20,
												height: 20,
												borderRadius: '10px',
												// border: '1px solid red',
												overflow: 'hidden',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												boxShadow: 1, // optional, for a subtle shadow
											}}
										>
											<input
												type="color"
												value={colorSettings[key]}
												className='color-input'
												onChange={(e) => setColorSettings({ ...colorSettings, [key]: e.target.value })}
												
											/>
										</Box>
										<TextField
											label={label}
											value={colorSettings[key]}
											onChange={(e) => setColorSettings({ ...colorSettings, [key]: e.target.value })}
											size="small"
											sx={{ width: 120 }}
											inputProps={{ maxLength: 7 }}
										/>
									</Stack>
								))}
							</Stack>
						</Box>
						
						{/* Apply Button */}
						<Box mt={2} display="flex" justifyContent="flex-end">
							<Button
								variant="contained"
								color="info"
								disabled={loading}
								onClick={async () => {
									const obj = {
										...JSON.parse(localStorage.getItem('navSettings')),
										colorSettings,
										options: navOrder
										        .filter(item => item && item.label)
										        .map(({ label, visible }) => ({ label, visible })),
									};
                                    
									// console.log('Saving navigation obj:', obj);
                                     
									// Call the backend API to save nav settings
									if (company?._id) {
										try {
											setLoading(true);
											const res = await CultService.setNavList({
												params: { companyId: company._id },
												data: { options: obj.options, colorSettings: obj.colorSettings },
											});
											// console.log('Navigation settings saved:', res);
											toast.success(res?.data?.message || 'Navigation settings saved successfully!');
											// Save to localStorage
											localStorage.setItem('navSettings', JSON.stringify(obj));
											// Notify other components
											window.dispatchEvent(new Event('navSettingsChanged'));
										} catch (err) {
											toast.error('Failed to save navigation settings to server.');
										} finally {
											setLoading(false);
										}
									} else {
										// toast.success('Navigation settings saved!');
									}

									// localStorage.setItem('navSettings', JSON.stringify(obj));
									// window.dispatchEvent(new Event('navSettingsChanged'));
								}}
							>
								Apply
							</Button>
						</Box>
					</Box>
				)}
			</Box>
		</Stack>
	);
}
